import { Box, Tooltip, Typography } from '@mui/material';
import { useCallback, type ChangeEvent, type KeyboardEvent, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { AppSwitch } from '@/UI/AppSwitch/AppSwitch';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { Input } from '@/UI/Input/Input';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { Flex } from '@/UI/styles';
import { AppTable } from '@/UI/AppTable/AppTable';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import type { ApiKeyProviderId, CredentialType } from './apiKeyProviders';
import type { ServerRow, GoogleServiceAccountDraft } from './apiKeysTable.types';

const textActionSx = {
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: '4px 8px',
  color: color.White80_T,
  font: 'inherit',
  '&:hover': { color: color.White100 },
} as const;

const fieldLabelSx = {
  fontFamily: '"DM Mono", ui-monospace, monospace',
  color: color.White64_T,
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
  width: 88,
} as const;

function maskKeyForDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('*')) {
    return trimmed;
  }
  if (trimmed.length <= 3) {
    return '•'.repeat(trimmed.length);
  }
  const dots = Math.min(12, trimmed.length - 3);
  return `${'•'.repeat(dots)}${trimmed.slice(-3)}`;
}

function ProviderBrandIcon({ providerId }: { providerId: ApiKeyProviderId }) {
  if (providerId === 'google') {
    return (
      <Box aria-hidden component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <GoogleLogo width={20} height={20} title="" />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src="/menu-images/fal100x100.png"
      alt=""
      aria-hidden
      sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
    />
  );
}

function ReadOnlyValue({ value, masked, inactive }: { value: string; masked: boolean; inactive: boolean }) {
  const display = masked ? maskKeyForDisplay(value) : value;
  return (
    <Box
      sx={{
        flex: '1 0 0',
        minWidth: 0,
        // Match `Input size="small"` control height to avoid layout jump when entering edit mode.
        minHeight: 26,
        px: 1,
        py: 0.5,
        borderRadius: '4px',
        border: `1px solid ${inactive ? color.White04_T : color.White08_T}`,
        bgcolor: color.Black92,
        display: 'flex',
        alignItems: 'center',
        opacity: inactive ? 0.55 : 1,
      }}
    >
      <Typography
        variant="body-sm-rg"
        component="span"
        sx={{
          fontFamily: '"DM Mono", ui-monospace, monospace',
          color: inactive ? color.White40_T : color.White80_T,
          userSelect: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

function ReadOnlyLabeledField({
  label,
  value,
  masked,
  inactive,
}: {
  label: string;
  value: string;
  masked: boolean;
  inactive: boolean;
}) {
  return (
    <Flex sx={{ alignItems: 'center', gap: 0.5, width: '100%' }}>
      <Typography variant="body-xs-rg" component="span" sx={fieldLabelSx}>
        {label}
      </Typography>
      <ReadOnlyValue value={value} masked={masked} inactive={inactive} />
    </Flex>
  );
}

function ReadOnlyCredentialsField({ row, inactive }: { row: ServerRow; inactive: boolean }) {
  const { t } = useTranslation();

  if (row.credentialType === 'apiKey') {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <ReadOnlyValue value={row.maskedApiKey} masked={true} inactive={inactive} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
      <ReadOnlyLabeledField
        label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_SERVICE_EMAIL_LABEL)}
        value={row.clientEmail}
        masked={false}
        inactive={inactive}
      />
      <ReadOnlyLabeledField
        label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PROJECT_ID_LABEL)}
        value={row.projectId}
        masked={false}
        inactive={inactive}
      />
      <ReadOnlyLabeledField
        label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PRIVATE_KEY_LABEL)}
        value={row.maskedPrivateKey}
        masked={true}
        inactive={inactive}
      />
    </Box>
  );
}

type ProviderDef = { id: ApiKeyProviderId; labelKey: string; credentialType: CredentialType };

export type ApiKeysProviderRowProps = {
  provider: ProviderDef;
  row: ServerRow | undefined;
  editing: boolean;
  draft: string | GoogleServiceAccountDraft;
  /** When there is no server row yet, reflects the next POST `isEnabled`. */
  pendingEnabled: boolean;
  isBusy: boolean;
  inputRefs: MutableRefObject<Partial<Record<ApiKeyProviderId, HTMLInputElement | null>>>;
  onDraftChange: (providerId: ApiKeyProviderId, value: string) => void;
  onDraftFieldChange: (providerId: ApiKeyProviderId, field: keyof GoogleServiceAccountDraft, value: string) => void;
  onSave: (providerId: ApiKeyProviderId) => void;
  onDelete: (providerId: ApiKeyProviderId) => void;
  onStartAdd: (providerId: ApiKeyProviderId) => void;
  onStartEdit: (providerId: ApiKeyProviderId) => void;
  onCancelEdit: (providerId: ApiKeyProviderId) => void;
  onStatusChange: (providerId: ApiKeyProviderId, nextOn: boolean) => void;
};

export const ApiKeysProviderRow = ({
  provider,
  row,
  editing,
  draft,
  pendingEnabled,
  isBusy,
  inputRefs,
  onDraftChange,
  onDraftFieldChange,
  onSave,
  onDelete,
  onStartAdd,
  onStartEdit,
  onCancelEdit,
  onStatusChange,
}: ApiKeysProviderRowProps) => {
  const { t } = useTranslation();

  const isServiceAccount = provider.credentialType === 'serviceAccount';
  const sa = isServiceAccount && typeof draft === 'object' && draft != null ? draft : null;

  const hasKey =
    row !== undefined &&
    (row.credentialType === 'serviceAccount' ? Boolean(row.clientEmail.trim()) : Boolean(row.maskedApiKey.trim()));
  const mode: 'empty' | 'edit' | 'readonly' = !hasKey && !editing ? 'empty' : editing ? 'edit' : 'readonly';

  const keyActive = hasKey ? (row?.isEnabled ?? true) : pendingEnabled;
  const statusSwitchChecked = !hasKey && editing ? keyActive : hasKey ? keyActive : false;

  const canSaveKey = isServiceAccount
    ? sa != null && Boolean(sa.clientEmail.trim()) && Boolean(sa.projectId.trim()) && Boolean(sa.privateKey.trim())
    : typeof draft === 'string' && Boolean(draft.trim());

  const providerId = provider.id;
  const isMultiLineRow =
    isServiceAccount && (mode === 'edit' || (mode === 'readonly' && row?.credentialType === 'serviceAccount'));

  const setInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRefs.current[providerId] = el;
    },
    [inputRefs, providerId],
  );

  const handleDraftChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onDraftChange(providerId, e.target.value);
    },
    [onDraftChange, providerId],
  );

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onDraftFieldChange(providerId, 'clientEmail', e.target.value),
    [onDraftFieldChange, providerId],
  );

  const handleProjectIdChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onDraftFieldChange(providerId, 'projectId', e.target.value),
    [onDraftFieldChange, providerId],
  );

  const handlePrivateKeyChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onDraftFieldChange(providerId, 'privateKey', e.target.value),
    [onDraftFieldChange, providerId],
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && canSaveKey) {
        e.preventDefault();
        onSave(providerId);
      }
    },
    [canSaveKey, onSave, providerId],
  );

  const handleStatusSwitchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onStatusChange(providerId, e.target.checked);
    },
    [onStatusChange, providerId],
  );

  const handleSaveClick = useCallback(() => {
    onSave(providerId);
  }, [onSave, providerId]);

  const handleCancelClick = useCallback(() => {
    onCancelEdit(providerId);
  }, [onCancelEdit, providerId]);

  const handleDeleteClick = useCallback(() => {
    onDelete(providerId);
  }, [onDelete, providerId]);

  const handleStartAdd = useCallback(() => {
    onStartAdd(providerId);
  }, [onStartAdd, providerId]);

  const handleStartEdit = useCallback(() => {
    onStartEdit(providerId);
  }, [onStartEdit, providerId]);

  const topAlignSx = isMultiLineRow ? { verticalAlign: 'top', pt: 1.5 } : {};
  const topControlStripSx = isMultiLineRow ? { mt: 0.5, height: 24 } : {};

  return (
    <AppTable.Tr
      hoverable
      sx={{
        '&:hover .api-keys-edit-btn, & .api-keys-edit-btn:focus-visible': {
          opacity: 1,
        },
      }}
    >
      <AppTable.Td sx={{ pl: 1.5, ...topAlignSx }}>
        <Flex sx={{ alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <ProviderBrandIcon providerId={provider.id} />
          <Typography variant="body-std-md" color={color.White80_T} noWrap>
            {t(provider.labelKey)}
          </Typography>
        </Flex>
      </AppTable.Td>
      <AppTable.Td sx={topAlignSx}>
        <Box sx={{ minWidth: 0 }}>
          {mode === 'edit' ? (
            isServiceAccount ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body-xs-rg" component="span" sx={fieldLabelSx}>
                    {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_SERVICE_EMAIL_LABEL)}
                  </Typography>
                  <Input
                    size="small"
                    placeholder={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_SERVICE_EMAIL_PLACEHOLDER)}
                    value={sa?.clientEmail ?? ''}
                    onChange={handleEmailChange}
                    inputRef={setInputRef}
                    disabled={isBusy}
                    sx={{ flex: '1 0 0', maxWidth: 'none', mt: 0 }}
                  />
                </Flex>
                <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body-xs-rg" component="span" sx={fieldLabelSx}>
                    {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PROJECT_ID_LABEL)}
                  </Typography>
                  <Input
                    size="small"
                    placeholder={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PROJECT_ID_PLACEHOLDER)}
                    value={sa?.projectId ?? ''}
                    onChange={handleProjectIdChange}
                    disabled={isBusy}
                    sx={{ flex: '1 0 0', maxWidth: 'none', mt: 0 }}
                  />
                </Flex>
                <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body-xs-rg" component="span" sx={fieldLabelSx}>
                    {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PRIVATE_KEY_LABEL)}
                  </Typography>
                  <Input
                    type="password"
                    autoComplete="off"
                    size="small"
                    placeholder={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.GOOGLE_PRIVATE_KEY_PLACEHOLDER)}
                    value={sa?.privateKey ?? ''}
                    onChange={handlePrivateKeyChange}
                    onKeyDown={handleInputKeyDown}
                    disabled={isBusy}
                    sx={{ flex: '1 0 0', maxWidth: 'none', mt: 0 }}
                  />
                </Flex>
              </Box>
            ) : (
              <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body-xs-rg" component="span" sx={fieldLabelSx}>
                  {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.API_KEY_LABEL)}
                </Typography>
                <Input
                  type="password"
                  autoComplete="off"
                  size="small"
                  placeholder={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.KEY_LABEL)}
                  value={typeof draft === 'string' ? draft : ''}
                  onChange={handleDraftChange}
                  onKeyDown={handleInputKeyDown}
                  inputRef={setInputRef}
                  disabled={isBusy}
                  sx={{ flex: '1 0 0', maxWidth: 'none', mt: 0 }}
                />
              </Flex>
            )
          ) : null}
          {mode === 'readonly' && row != null ? <ReadOnlyCredentialsField row={row} inactive={!keyActive} /> : null}
        </Box>
      </AppTable.Td>
      <AppTable.Td sx={topAlignSx}>
        {mode === 'empty' ? null : (
          <Flex sx={{ justifyContent: 'flex-start', alignItems: 'center', ...topControlStripSx }}>
            <Tooltip
              title={
                statusSwitchChecked
                  ? t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.DISABLE_TOOLTIP)
                  : t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.ENABLE_TOOLTIP)
              }
            >
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <AppSwitch
                  checked={statusSwitchChecked}
                  onChange={handleStatusSwitchChange}
                  disabled={isBusy}
                  slotProps={{
                    input: {
                      'aria-label': t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.COLUMN_STATUS),
                    },
                  }}
                />
              </Box>
            </Tooltip>
          </Flex>
        )}
      </AppTable.Td>
      <AppTable.Td align="right" sx={topAlignSx}>
        <Flex sx={{ alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, ...topControlStripSx }}>
          {mode === 'empty' ? (
            <Tooltip title={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.ADD_TOOLTIP)}>
              <span>
                <AppIconButton
                  type="button"
                  aria-label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.ADD_KEY_ARIA)}
                  onClick={handleStartAdd}
                  disabled={isBusy}
                  width={32}
                  height={32}
                >
                  <PlusIcon width={18} height={18} />
                </AppIconButton>
              </span>
            </Tooltip>
          ) : null}

          {mode === 'edit' ? (
            <>
              <Typography
                component="button"
                type="button"
                variant="body-std-md"
                disabled={!canSaveKey || isBusy}
                onClick={handleSaveClick}
                sx={{
                  ...textActionSx,
                  '&:disabled': {
                    opacity: 0.4,
                    cursor: 'not-allowed',
                    color: color.White64_T,
                  },
                }}
              >
                {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.SAVE)}
              </Typography>
              <Typography
                component="button"
                type="button"
                variant="body-std-md"
                disabled={isBusy}
                onClick={handleCancelClick}
                sx={textActionSx}
              >
                {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.CANCEL)}
              </Typography>
              <Tooltip title={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.DELETE_TOOLTIP)}>
                <span>
                  <AppIconButton
                    type="button"
                    aria-label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.DELETE_KEY_ARIA)}
                    onClick={handleDeleteClick}
                    disabled={isBusy}
                    width={32}
                    height={32}
                  >
                    <i className="fa-light fa-trash" style={{ fontSize: 14 }} />
                  </AppIconButton>
                </span>
              </Tooltip>
            </>
          ) : null}

          {mode === 'readonly' ? (
            <>
              <Typography
                className="api-keys-edit-btn"
                component="button"
                type="button"
                variant="body-std-md"
                onClick={handleStartEdit}
                disabled={isBusy}
                sx={{
                  ...textActionSx,
                  opacity: 0,
                  transition: 'opacity 0.15s ease',
                }}
              >
                {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.EDIT)}
              </Typography>
              <Tooltip title={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.DELETE_TOOLTIP)}>
                <span>
                  <AppIconButton
                    type="button"
                    aria-label={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.API_KEYS.DELETE_KEY_ARIA)}
                    onClick={handleDeleteClick}
                    disabled={isBusy}
                    width={32}
                    height={32}
                  >
                    <i className="fa-light fa-trash" style={{ fontSize: 14 }} />
                  </AppIconButton>
                </span>
              </Tooltip>
            </>
          ) : null}
        </Flex>
      </AppTable.Td>
    </AppTable.Tr>
  );
};
